import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  MapPin,
  Trash2,
  Edit3,
  CalendarDays,
  Link2,
  RefreshCw,
  AlertCircle,
  Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_COLORS = [
  { value: '#0071E3', label: 'Blue' },
  { value: '#10B981', label: 'Green' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#EF4444', label: 'Red' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#1B365D', label: 'Navy' },
];

const MicrosoftIcon = () => (
  <svg viewBox="0 0 21 21" className="w-4 h-4">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const formatTime = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatDate = (isoStr) => {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

const isSameDay = (d1, d2) => {
  const a = new Date(d1);
  const b = new Date(d2);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

// Event form modal
const EventModal = ({ open, onClose, event, onSave, onDelete }) => {
  const [form, setForm] = useState({
    title: '', description: '', start_time: '', end_time: '',
    all_day: false, location: '', color: '#0071E3'
  });

  useEffect(() => {
    if (event) {
      setForm({
        title: event.title || '',
        description: event.description || '',
        start_time: event.start_time ? event.start_time.slice(0, 16) : '',
        end_time: event.end_time ? event.end_time.slice(0, 16) : '',
        all_day: event.all_day || false,
        location: event.location || '',
        color: event.color || '#0071E3'
      });
    } else {
      setForm({ title: '', description: '', start_time: '', end_time: '', all_day: false, location: '', color: '#0071E3' });
    }
  }, [event, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title || !form.start_time) {
      toast.error('Title and start time are required');
      return;
    }
    const payload = {
      ...form,
      start_time: new Date(form.start_time).toISOString(),
      end_time: form.end_time ? new Date(form.end_time).toISOString() : new Date(new Date(form.start_time).getTime() + 3600000).toISOString()
    };
    onSave(payload);
  };

  const isEditing = event?.id;
  const isMicrosoftEvent = event?.source === 'microsoft';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="event-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isMicrosoftEvent ? 'Microsoft 365 Event' : isEditing ? 'Edit Event' : 'New Event'}
          </DialogTitle>
        </DialogHeader>
        {isMicrosoftEvent ? (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
              <MicrosoftIcon />
              <span>Synced from Microsoft 365</span>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Title</Label>
              <p className="font-medium">{event.title}</p>
            </div>
            {event.description && (
              <div>
                <Label className="text-xs text-slate-500">Description</Label>
                <p className="text-sm text-slate-600">{event.description}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Start</Label>
                <p className="text-sm">{formatDate(event.start_time)}</p>
                <p className="text-sm text-slate-500">{formatTime(event.start_time)}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">End</Label>
                <p className="text-sm">{formatDate(event.end_time)}</p>
                <p className="text-sm text-slate-500">{formatTime(event.end_time)}</p>
              </div>
            </div>
            {event.location && (
              <div>
                <Label className="text-xs text-slate-500">Location</Label>
                <p className="text-sm flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</p>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Meeting with client"
                required
                data-testid="event-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start *</Label>
                <Input
                  type="datetime-local"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  required
                  data-testid="event-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End</Label>
                <Input
                  type="datetime-local"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  data-testid="event-end-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.all_day}
                onCheckedChange={(v) => setForm({ ...form, all_day: v })}
                data-testid="event-allday-toggle"
              />
              <Label>All day event</Label>
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Office, Zoom link, etc."
                data-testid="event-location-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Event details..."
                className="min-h-[80px]"
                data-testid="event-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2">
                {EVENT_COLORS.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setForm({ ...form, color: c.value })}
                    className={`w-7 h-7 rounded-full border-2 transition-transform ${form.color === c.value ? 'border-slate-900 scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c.value }}
                    title={c.label}
                    data-testid={`color-${c.label.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>
            <div className="flex justify-between pt-4">
              <div>
                {isEditing && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => onDelete(event.id)}
                    data-testid="delete-event-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                <Button type="submit" className="bg-[#1B365D] hover:bg-[#1B365D]/90" data-testid="save-event-btn">
                  {isEditing ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Calendar = () => {
  const { getAuthHeaders } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msStatus, setMsStatus] = useState({ connected: false });
  const [syncing, setSyncing] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [view, setView] = useState('month');

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const today = new Date();

  const fetchEvents = useCallback(async () => {
    try {
      const start = new Date(year, month, 1).toISOString();
      const end = new Date(year, month + 1, 0, 23, 59, 59).toISOString();
      const response = await axios.get(`${API_URL}/api/calendar/events?start=${start}&end=${end}`, getAuthHeaders());
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [year, month, getAuthHeaders]);

  const checkMsStatus = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/calendar/microsoft/status`, getAuthHeaders());
      setMsStatus(resp.data);
    } catch (error) {
      console.error('Failed to check MS status:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { checkMsStatus(); }, [checkMsStatus]);

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(year, month + direction, 1));
  };

  const goToToday = () => setCurrentDate(new Date());

  const handleSaveEvent = async (payload) => {
    try {
      if (editingEvent?.id) {
        await axios.put(`${API_URL}/api/calendar/events/${editingEvent.id}`, payload, getAuthHeaders());
        toast.success('Event updated');
      } else {
        await axios.post(`${API_URL}/api/calendar/events`, payload, getAuthHeaders());
        toast.success('Event created');
      }
      setShowEventModal(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API_URL}/api/calendar/events/${eventId}`, getAuthHeaders());
      toast.success('Event deleted');
      setShowEventModal(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleSyncMicrosoft = async () => {
    setSyncing(true);
    try {
      const resp = await axios.post(`${API_URL}/api/calendar/microsoft/sync`, {}, getAuthHeaders());
      toast.success(resp.data.message);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleConnectMicrosoft = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/calendar/microsoft/connect`, getAuthHeaders());
      window.location.href = resp.data.auth_url;
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to connect';
      toast.error(msg);
    }
  };

  const handleDisconnectMicrosoft = async () => {
    if (!window.confirm('Disconnect Microsoft calendar? Synced events will be removed.')) return;
    try {
      await axios.delete(`${API_URL}/api/calendar/microsoft/disconnect`, getAuthHeaders());
      toast.success('Microsoft calendar disconnected');
      setMsStatus({ connected: false });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const openNewEvent = (date) => {
    const d = date || new Date();
    const start = new Date(d);
    start.setHours(9, 0, 0, 0);
    setEditingEvent({ start_time: start.toISOString() });
    setShowEventModal(true);
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setShowEventModal(true);
  };

  // Calendar grid
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const prevMonthDays = getDaysInMonth(year, month - 1);
  const monthName = currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getEventsForDay = (day) => {
    const dateStr = new Date(year, month, day);
    return events.filter(e => isSameDay(e.start_time, dateStr));
  };

  // Week view data
  const getWeekDays = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  };

  const selectedDayEvents = selectedDate ? getEventsForDay(selectedDate.getDate()) : [];

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Calendar</h1>
          <p className="text-slate-500 mt-1">Manage your schedule and sync with Microsoft 365</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Microsoft Connection Status */}
          {msStatus.connected ? (
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-700 gap-1" data-testid="ms-connected-badge">
                <Check className="w-3 h-3" />
                <MicrosoftIcon />
                Connected
              </Badge>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={handleSyncMicrosoft}
                disabled={syncing}
                data-testid="sync-calendar-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDisconnectMicrosoft}
                className="text-slate-500"
                data-testid="disconnect-ms-btn"
              >
                Disconnect
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              className="rounded-full gap-2"
              onClick={handleConnectMicrosoft}
              data-testid="connect-ms-btn"
            >
              <MicrosoftIcon />
              Connect Microsoft 365
            </Button>
          )}
          <Button
            className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full"
            onClick={() => openNewEvent(selectedDate || new Date())}
            data-testid="add-event-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Event
          </Button>
        </div>
      </div>

      {/* Calendar Navigation */}
      <Card className="border-0 shadow-apple" data-testid="calendar-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)} data-testid="prev-month-btn">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="font-heading text-xl font-semibold text-[#1B365D] min-w-[200px] text-center" data-testid="current-month">
                {monthName}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)} data-testid="next-month-btn">
                <ChevronRight className="w-5 h-5" />
              </Button>
              <Button variant="outline" size="sm" className="rounded-full" onClick={goToToday} data-testid="today-btn">
                Today
              </Button>
            </div>
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              <button
                onClick={() => setView('month')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
                data-testid="month-view-btn"
              >
                Month
              </button>
              <button
                onClick={() => setView('week')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === 'week' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
                data-testid="week-view-btn"
              >
                Week
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-pulse text-slate-500">Loading calendar...</div>
            </div>
          ) : view === 'month' ? (
            <div>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-px mb-2">
                {dayNames.map(d => (
                  <div key={d} className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-2">
                    {d}
                  </div>
                ))}
              </div>
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
                {/* Previous month padding */}
                {Array.from({ length: firstDay }, (_, i) => (
                  <div key={`prev-${i}`} className="bg-slate-50 min-h-[100px] p-2">
                    <span className="text-xs text-slate-300">{prevMonthDays - firstDay + i + 1}</span>
                  </div>
                ))}
                {/* Current month days */}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const dayEvents = getEventsForDay(day);
                  const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
                  const isSelected = selectedDate && selectedDate.getDate() === day && selectedDate.getMonth() === month;

                  return (
                    <div
                      key={day}
                      className={`bg-white min-h-[100px] p-2 cursor-pointer transition-colors hover:bg-slate-50 ${isSelected ? 'ring-2 ring-[#0071E3] ring-inset' : ''}`}
                      onClick={() => setSelectedDate(new Date(year, month, day))}
                      onDoubleClick={() => openNewEvent(new Date(year, month, day))}
                      data-testid={`day-${day}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full ${
                          isToday ? 'bg-[#0071E3] text-white' : 'text-slate-700'
                        }`}>
                          {day}
                        </span>
                        {dayEvents.length > 0 && (
                          <span className="text-[10px] text-slate-400">{dayEvents.length}</span>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map(ev => (
                          <div
                            key={ev.id}
                            onClick={(e) => { e.stopPropagation(); openEditEvent(ev); }}
                            className="text-[11px] leading-tight truncate px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity text-white"
                            style={{ backgroundColor: ev.color || '#0071E3' }}
                            title={ev.title}
                            data-testid={`event-${ev.id}`}
                          >
                            {ev.source === 'microsoft' && <span className="mr-0.5">M</span>}
                            {ev.title}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-[10px] text-slate-400 pl-1">+{dayEvents.length - 3} more</div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Next month padding */}
                {Array.from({ length: (7 - ((firstDay + daysInMonth) % 7)) % 7 }, (_, i) => (
                  <div key={`next-${i}`} className="bg-slate-50 min-h-[100px] p-2">
                    <span className="text-xs text-slate-300">{i + 1}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Week View
            <div>
              <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
                {getWeekDays().map(day => {
                  const dayEvents = events.filter(e => isSameDay(e.start_time, day));
                  const isToday = isSameDay(day, today);
                  return (
                    <div key={day.toISOString()} className="bg-white min-h-[400px] p-3">
                      <div className="text-center mb-3">
                        <div className="text-xs text-slate-500 uppercase">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                        <div className={`text-lg font-semibold mt-1 w-9 h-9 flex items-center justify-center rounded-full mx-auto ${isToday ? 'bg-[#0071E3] text-white' : 'text-slate-700'}`}>
                          {day.getDate()}
                        </div>
                      </div>
                      <div className="space-y-1">
                        {dayEvents.map(ev => (
                          <div
                            key={ev.id}
                            onClick={() => openEditEvent(ev)}
                            className="text-xs p-2 rounded-lg cursor-pointer hover:opacity-80 text-white"
                            style={{ backgroundColor: ev.color || '#0071E3' }}
                          >
                            <p className="font-medium truncate">{ev.title}</p>
                            <p className="opacity-75">{formatTime(ev.start_time)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Day Detail Panel */}
      {selectedDate && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-0 shadow-apple">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg text-[#1B365D] flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" />
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </CardTitle>
                <Button
                  size="sm"
                  className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full"
                  onClick={() => openNewEvent(selectedDate)}
                  data-testid="add-event-day-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Event
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {selectedDayEvents.length > 0 ? (
                <div className="space-y-3">
                  {selectedDayEvents.map(ev => (
                    <div
                      key={ev.id}
                      onClick={() => openEditEvent(ev)}
                      className="flex items-start gap-4 p-4 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors border border-slate-100"
                      data-testid={`day-event-${ev.id}`}
                    >
                      <div className="w-1 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: ev.color || '#0071E3' }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-slate-900">{ev.title}</h4>
                          {ev.source === 'microsoft' && (
                            <Badge variant="outline" className="text-[10px] gap-1 px-1.5">
                              <MicrosoftIcon /> M365
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {ev.all_day ? 'All day' : `${formatTime(ev.start_time)} - ${formatTime(ev.end_time)}`}
                          </span>
                          {ev.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {ev.location}
                            </span>
                          )}
                        </div>
                        {ev.description && (
                          <p className="text-sm text-slate-400 mt-1 line-clamp-2">{ev.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No events for this day</p>
                  <p className="text-xs mt-1">Double-click a day or use the + button to add an event</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Info Banner */}
      {!msStatus.connected && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-[#1B365D]/5 to-[#0071E3]/5">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#0071E3]/10 rounded-xl">
              <MicrosoftIcon />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-[#1B365D]">Connect Microsoft 365</h3>
              <p className="text-sm text-slate-500">Sync your Outlook calendar to see all events in one place. Requires valid Azure AD credentials.</p>
            </div>
            <Button variant="outline" className="rounded-full" onClick={handleConnectMicrosoft} data-testid="connect-ms-banner-btn">
              Connect
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Event Modal */}
      <EventModal
        open={showEventModal}
        onClose={() => { setShowEventModal(false); setEditingEvent(null); }}
        event={editingEvent}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
      />
    </div>
  );
};

export default Calendar;
